import React, {
	useCallback,
	type ElementType,
	useMemo,
	type MouseEvent,
	type KeyboardEvent,
} from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { CustomThemeButton } from '@atlaskit/button';
import WatchIcon from '@atlaskit/icon/core/migration/eye-open--watch';
import { token } from '@atlaskit/tokens';
import ToolTip from '@atlaskit/tooltip';
import { useIntl } from '@atlassian/jira-intl';
import { ATTACHMENT_PARENT } from '@atlassian/jira-issue-attachments-store/src/common/constants.tsx';
import type {
	AttachmentId,
	AttachmentParent,
} from '@atlassian/jira-issue-attachments-store/src/common/types.tsx';
import { useFieldConfig } from '@atlassian/jira-issue-field-base/src/services/field-config-service/main.tsx';
import { useIssueScroll } from '@atlassian/jira-issue-scroll/src/services/main.tsx';
import {
	type ScrollTargetType,
	SCROLL_TARGET_TYPES,
} from '@atlassian/jira-issue-scroll/src/services/types.tsx';

import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';

import type { IssueKey } from '@atlassian/jira-shared-types/src/general.tsx';

import { type AttachmentView, filmStripView, listView } from '../types.tsx';
import messages from './messages.tsx';

// @ts-expect-error - TS7006 - Parameter 'fieldConfig' implicitly has an 'any' type.
const getFieldTitle = (attachmentParent: AttachmentParent, fieldConfig) => {
	if (attachmentParent !== ATTACHMENT_PARENT.customfield) {
		return attachmentParent;
	}

	return (fieldConfig && fieldConfig.title) || 'custom field';
};

export type Props = {
	issueKey: IssueKey;
	attachmentParent: AttachmentParent;
	attachmentParentId: string;
	attachmentView: AttachmentView;
	component: ElementType;
	attachmentId: AttachmentId;
	onScrollToComments: () => void;
	onScrollToWorklogs: () => void;
};

export const GoToButton = ({
	attachmentParent,
	attachmentParentId,
	onScrollToComments,
	onScrollToWorklogs,
	attachmentView,
	issueKey,
	component,
	attachmentId,
}: Props) => {
	const { formatMessage } = useIntl();
	const [, { setScrollTarget }] = useIssueScroll();
	const [{ value: fieldConfig }] = useFieldConfig(issueKey, `customfield_${attachmentParentId}`);
	const isDisabled =
		attachmentParent !== ATTACHMENT_PARENT.comment &&
		attachmentParent !== ATTACHMENT_PARENT.worklog;

	const onClick = useCallback(
		(event: MouseEvent<HTMLElement>, analyticsEvent: UIAnalyticsEvent) => {
			event.stopPropagation();
			event.preventDefault();

			const setAndScrollToTarget = (
				scrollTargetType: ScrollTargetType,
				scrollToTargetAction: () => void,
			) => {
				setScrollTarget({
					type: scrollTargetType,
					id: attachmentParentId,
				});
				scrollToTargetAction();
			};

			switch (attachmentParent) {
				case ATTACHMENT_PARENT.comment: {
					setAndScrollToTarget(SCROLL_TARGET_TYPES.COMMENT, onScrollToComments);
					fireUIAnalytics(analyticsEvent, 'gotoComment', {
						view: attachmentView,
					});
					break;
				}
				case ATTACHMENT_PARENT.worklog: {
					setAndScrollToTarget(SCROLL_TARGET_TYPES.WORKLOG, onScrollToWorklogs);
					fireUIAnalytics(analyticsEvent, 'gotoWorklog', {
						view: attachmentView,
					});
					break;
				}
				default:
					break;
			}
		},
		[
			attachmentParent,
			setScrollTarget,
			attachmentParentId,
			onScrollToComments,
			attachmentView,
			onScrollToWorklogs,
		],
	);

	const onKeyPress = useCallback((event: KeyboardEvent) => {
		if (event.key === 'Enter') {
			event.stopPropagation();
		}
	}, []);

	const toolTipContent = useMemo(() => {
		switch (attachmentParent) {
			case ATTACHMENT_PARENT.comment:
				return formatMessage(messages.goToComment);
			case ATTACHMENT_PARENT.worklog:
				return formatMessage(messages.goToWorklog);
			default:
				return formatMessage(messages.goToField, {
					field: getFieldTitle(attachmentParent, fieldConfig),
				});
		}
	}, [attachmentParent, fieldConfig, formatMessage]);

	const getButtonHoverColor = () => {
		if (isDisabled) {
			return {};
		}

		// since filmstrip action button has its own hover css, we will match the color they use
		return attachmentView === listView
			? {
					background: token('color.background.neutral.hovered'),
				}
			: {
					background: token('color.background.neutral.hovered'),
				};
	};

	return (
		<ToolTip content={toolTipContent} position="top">
			<CustomThemeButton
				testId={`issue-attachments-base.ui.goto-button.custom-theme-button.${attachmentId}`}
				appearance="subtle"
				iconBefore={
					<WatchIcon
						label={toolTipContent}
						color={isDisabled ? token('color.icon.disabled') : token('color.icon')}
					/>
				}
				onClick={onClick}
				onKeyPress={onKeyPress}
				// @ts-expect-error - TS2322 - Type '(current: (props: ThemeProps) => ThemeTokens, themeProps: ThemeProps) => { accentColor?: string | string[] | undefined; alignContent?: string | string[] | undefined; ... 909 more ...; buttonStyles: { ...; }; }' is not assignable to type '(current: (props: ThemeProps) => ThemeTokens, props: ThemeProps) => ThemeTokens'.
				theme={(current, themeProps) => {
					const { buttonStyles, spinnerStyles } = current(themeProps);
					return {
						buttonStyles: {
							...buttonStyles,
							display: 'flex',
							justifyContent: 'center',
							'&:hover': getButtonHoverColor(),
							...(attachmentView === filmStripView
								? {
										paddingLeft: '0px',
										paddingRight: '0px',
									}
								: {}),

							margin: '0',
						},
						...spinnerStyles,
					};
				}}
				isDisabled={isDisabled}
				component={component}
				interactionName="auto-generated.jira-issue-attachments-base.ui.goto-button.button"
			/>
		</ToolTip>
	);
};

GoToButton.defaultProps = {
	component: 'button',
};

export default GoToButton;
